/**
 * Les utilisateurs rassembles par SEL, pour l'ecran des droits.
 *
 * Nicolas, 22/09 a 07h25 : « donne moi acces a un tableau de bord ou je peux
 * activer les user ayant acces a ces fonctions. J'imagine un grand tableau
 * avec l'ensemble des SEL (ET NON L'ENSEMBLE DES LABORATOIRES) et en dessous
 * l'ensemble des utilisateurs et leurs droits associes. »
 *
 * La parenthese est la demande. L'ecran d'avant listait 548 fiches a plat,
 * avec pour seule colonne de laboratoire l'IDENTIFIANT Firestore du labo
 * (`23DXxL6dGXr73vywDJcf`) : on ne pouvait ni trouver quelqu'un, ni voir qui
 * avait quoi. Regrouper par laboratoire n'aurait pas suffi non plus — il y en
 * a 39, et Nicolas raisonne par SEL.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LA SEL N'EST PAS UN OBJET, C'EST UNE CHAINE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Verifie le 22/09 contre la base : la collection `structures` est VIDE. La
 * SEL n'existe que comme champ texte `structure` sur `laboratories/{id}`, et
 * les 39 laboratoires portent 16 valeurs distinctes, ecrites a la main : on y
 * trouve « NA », « HDF », mais aussi « Charentes » en minuscules quand les
 * autres sont en capitales, et « dead », « GESTION », « POLO ».
 *
 * On ne corrige donc RIEN ici — regrouper « Charentes » avec un hypothetique
 * « CHARENTES » serait deviner. On se contente de ne pas aggraver : une SEL
 * absente ou vide devient un groupe nomme, visible, plutot qu'un trou.
 */

/** Le nom du groupe des fiches qu'aucun laboratoire ne rattache. */
export const SANS_SEL = "Sans SEL";

/** Le nom du groupe des fiches dont le laboratoire n'existe plus. */
export const LABO_INCONNU = "Laboratoire inconnu";

/**
 * L'identifiant du laboratoire d'une fiche.
 *
 * `belongs_to` est une REFERENCE de document partout dans le back-office, et
 * c'est ce qui compte : `.id` suffit. Mais des fiches plus anciennes portent
 * une chaine, et d'autres une reference dont seul `path` a survecu au passage
 * par le cache — les trois formes se croisent dans la meme collection, alors
 * on les lit toutes les trois plutot que de perdre la fiche.
 */
export function idLaboratoire(fiche) {
  const ref = fiche?.belongs_to;
  if (!ref) return null;
  if (typeof ref === "string") return ref.split("/").filter(Boolean).pop() || null;
  if (typeof ref.id === "string") return ref.id;
  if (typeof ref.path === "string") return ref.path.split("/").filter(Boolean).pop() || null;
  return null;
}

/** La SEL d'un laboratoire, ou `null` si le champ est vide. */
export function selDuLaboratoire(labo) {
  const brut = labo?.structure;
  if (typeof brut !== "string") return null;
  const propre = brut.trim();
  return propre ? propre : null;
}

/**
 * Le nom de la SEL sous laquelle ranger une fiche.
 *
 * Trois cas, et ils se distinguent : pas de laboratoire du tout, un
 * laboratoire qui n'est plus dans la base, ou un laboratoire sans SEL. Les
 * confondre ferait un groupe fourre-tout ou l'on ne saurait pas quoi reparer.
 */
export function selDeLaFiche(fiche, laboParId) {
  const id = idLaboratoire(fiche);
  if (!id) return SANS_SEL;
  const labo = laboParId?.[id];
  if (!labo) return LABO_INCONNU;
  return selDuLaboratoire(labo) || SANS_SEL;
}

/** Le nom lisible d'un laboratoire, l'identifiant ne disant rien a personne. */
export function nomLaboratoire(fiche, laboParId) {
  const id = idLaboratoire(fiche);
  if (!id) return "—";
  const labo = laboParId?.[id];
  if (!labo) return `${id} (supprimé)`;
  return labo.name || id;
}

/** Les laboratoires indexes par identifiant, pour ne pas chercher en boucle. */
export function indexerLaboratoires(laboratoires) {
  const index = {};
  for (const labo of laboratoires || []) {
    const id = labo?.value || labo?.id;
    if (id) index[id] = labo;
  }
  return index;
}

/**
 * Vrai si la fiche correspond a la recherche.
 *
 * Sur 548 fiches, une recherche est la seule facon d'arriver quelque part.
 * Elle porte sur le nom, l'adresse et le laboratoire — les trois choses
 * qu'on connait quand on cherche quelqu'un. Accents et casse ignores : on
 * tape « begles », pas « Bègles ».
 */
export function correspond(fiche, laboParId, recherche) {
  const q = sansAccent(recherche);
  if (!q) return true;
  const champs = [fiche?.name, fiche?.id, nomLaboratoire(fiche, laboParId), selDeLaFiche(fiche, laboParId)];
  return champs.some((c) => sansAccent(c).includes(q));
}

function sansAccent(valeur) {
  return String(valeur || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Le tableau : une entree par SEL, chacune avec ses fiches.
 *
 * L'ordre est alphabetique, sauf les deux groupes d'anomalie qui passent en
 * dernier. Ils ne sont PAS caches : « Laboratoire inconnu » non vide veut dire
 * que des fiches pointent vers un laboratoire supprime, et c'est justement ce
 * qu'on veut voir depuis cet ecran-la.
 */
export function grouperParSel(fiches, laboratoires, recherche = "") {
  const laboParId = indexerLaboratoires(laboratoires);
  const groupes = new Map();

  for (const fiche of fiches || []) {
    if (!correspond(fiche, laboParId, recherche)) continue;
    const sel = selDeLaFiche(fiche, laboParId);
    if (!groupes.has(sel)) groupes.set(sel, []);
    groupes.get(sel).push(fiche);
  }

  const aLaFin = [SANS_SEL, LABO_INCONNU];
  return [...groupes.entries()]
    .map(([sel, membres]) => ({
      sel,
      membres: membres.sort((a, b) => {
        const parLabo = nomLaboratoire(a, laboParId).localeCompare(
          nomLaboratoire(b, laboParId),
          "fr",
          { numeric: true }
        );
        if (parLabo !== 0) return parLabo;
        return String(a?.name || a?.id || "").localeCompare(
          String(b?.name || b?.id || ""),
          "fr",
          { numeric: true }
        );
      }),
      // Ce que Nicolas veut voir en fermant le groupe : combien de personnes
      // ici ont recu au moins un droit.
      avecDroits: membres.filter((f) => (f?.sectionsAutorisees || []).length > 0).length,
    }))
    .sort((a, b) => {
      const ra = aLaFin.indexOf(a.sel);
      const rb = aLaFin.indexOf(b.sel);
      if (ra !== rb) return (ra === -1 ? -1 : ra) - (rb === -1 ? -1 : rb);
      return a.sel.localeCompare(b.sel, "fr", { numeric: true });
    });
}

export default {
  SANS_SEL,
  LABO_INCONNU,
  correspond,
  grouperParSel,
  idLaboratoire,
  indexerLaboratoires,
  nomLaboratoire,
  selDeLaFiche,
  selDuLaboratoire,
};
